import { useState, useCallback, memo, useMemo } from 'react';
import { RocketIcon, UsersIcon } from 'lucide-react';
import type { ScoreTabCardProps, ScoreTabButtonProps, ScoreCategory } from './ScoreTabCard.types';
import { useTranslation } from 'react-i18next';

/**
 * ScoreTabButton - A button for selecting score categories
 * Memoized to prevent unnecessary re-renders when active state doesn't change
 */
const ScoreTabButton = memo(function ScoreTabButton({
  label,
  isActive,
  onClick,
  onKeyDown,
}: ScoreTabButtonProps) {
  const slug = label.toLowerCase().replace(/\s+/g, '-');

  return (
    <button
      type="button"
      onClick={onClick}
      onKeyDown={onKeyDown}
      role="tab"
      aria-selected={isActive}
      aria-controls={`score-panel-${slug}`}
      id={`score-tab-${slug}`}
      tabIndex={isActive ? 0 : -1}
      className={`flex h-9 cursor-pointer items-center justify-center rounded-lg border px-4 py-2 transition-colors duration-base ease-out hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
        isActive ? 'border-border-strong bg-accent' : 'border-border-light bg-transparent'
      }`}
    >
      <span className="text-label-md font-medium whitespace-nowrap text-foreground">{label}</span>
    </button>
  );
});

interface ScoreTabButtonWrapperProps {
  category: ScoreCategory;
  index: number;
  activeCategory: ScoreCategory;
  onCategoryChange: (category: ScoreCategory) => void;
  onKeyDown: (event: React.KeyboardEvent, currentIndex: number) => void;
}

/**
 * ScoreTabButtonWrapper - Wrapper to handle keyboard navigation per button
 * Memoized to prevent unnecessary re-renders
 */
const ScoreTabButtonWrapper = memo(function ScoreTabButtonWrapper({
  category,
  index,
  activeCategory,
  onCategoryChange,
  onKeyDown,
}: ScoreTabButtonWrapperProps) {
  const handleClick = useCallback(() => {
    onCategoryChange(category);
  }, [onCategoryChange, category]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      onKeyDown(e, index);
    },
    [onKeyDown, index],
  );

  return (
    <ScoreTabButton
      label={category}
      isActive={activeCategory === category}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    />
  );
});

/**
 * ScoreTabCard - Displays score information with tabbed category selection
 *
 * Features:
 * - Tabbed interface for switching between score categories
 * - Accessible with proper ARIA roles and keyboard navigation
 * - Responsive layout
 * - Memoized to prevent unnecessary re-renders
 */
export const ScoreTabCard = memo(function ScoreTabCard({
  type,
  scores,
  title,
  isExporting = false,
}: ScoreTabCardProps) {
  const { t } = useTranslation('contentTest');
  // eslint-disable-next-line i18next/no-literal-string -- compared against `score.title` from the payload; the rendered tab labels come from the data
  const [activeCategory, setActiveCategory] = useState<ScoreCategory>('General');

  // Find the score for the active category
  const activeScore = useMemo(() => {
    return scores.find((s) => s.title.toLowerCase() === activeCategory.toLowerCase());
  }, [scores, activeCategory]);

  // Memoize display title
  const displayTitle = useMemo(() => {
    return (
      title ||
      (type === 'audience'
        ? t('ideationWidget.scoreTabCard.audienceScore')
        : t('ideationWidget.scoreTabCard.creativeScore'))
    );
  }, [title, type, t]);

  const categoryOptions = useMemo(() => {
    return scores.map((s) => s.title);
  }, [scores]);

  // Memoize icon selection
  const Icon = type === 'audience' ? UsersIcon : RocketIcon;

  // Memoize category change handler
  const handleCategoryChange = useCallback((category: ScoreCategory) => {
    setActiveCategory(category);
  }, []);

  // Memoize keyboard navigation handler
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, currentIndex: number) => {
      let newIndex = currentIndex;

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        newIndex = (currentIndex + 1) % categoryOptions.length;
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        newIndex = (currentIndex - 1 + categoryOptions.length) % categoryOptions.length;
      } else if (event.key === 'Home') {
        event.preventDefault();
        newIndex = 0;
      } else if (event.key === 'End') {
        event.preventDefault();
        newIndex = categoryOptions.length - 1;
      }

      if (newIndex !== currentIndex) {
        setActiveCategory(categoryOptions[newIndex]);
      }
    },
    [categoryOptions],
  );

  // Memoize IDs for accessibility
  const cardId = `score-card-${type}`;
  const activePanelId = useMemo(() => {
    return `score-panel-${activeCategory.toLowerCase().replace(/\s+/g, '-')}`;
  }, [activeCategory]);

  const activeTabId = useMemo(() => {
    return `score-tab-${activeCategory.toLowerCase().replace(/\s+/g, '-')}`;
  }, [activeCategory]);

  // Memoize description text
  const descriptionText =
    activeScore?.description || t('ideationWidget.scoreTabCard.noDescriptionAvailable');

  return (
    <article
      aria-labelledby={`${cardId}-title`}
      className="flex flex-1 flex-col gap-10 rounded-3xl bg-surface p-10"
    >
      {/* Header section */}
      <div className="flex flex-col gap-8">
        {/* Icon and Title */}
        <div className="flex flex-col gap-6">
          <div className="flex flex-row gap-2">
            <Icon aria-hidden="true" className="size-8 shrink-0 text-muted-foreground" />
            <h3 id={`${cardId}-title`} className="text-heading-xl text-foreground">
              {displayTitle}
            </h3>
          </div>

          {/* Category tabs */}
          {!isExporting && (
            <div
              role="tablist"
              aria-label={t('ideationWidget.scoreTabCard.categoriesLabel', {
                title: displayTitle,
              })}
              className="flex flex-wrap gap-2"
            >
              {categoryOptions.map((category, index) => (
                <ScoreTabButtonWrapper
                  key={category}
                  category={category}
                  index={index}
                  activeCategory={activeCategory}
                  onCategoryChange={handleCategoryChange}
                  onKeyDown={handleKeyDown}
                />
              ))}
            </div>
          )}
        </div>

        {/* Export: every category listed (label + description); live: the active tab's panel. */}
        {isExporting ? (
          <div className="flex flex-col gap-6">
            {scores.map((score, index) => (
              <div key={`${score.title}-${index}`} className="flex flex-col gap-2">
                <p className="text-label-md font-medium text-foreground">{score.title}</p>
                <p className="text-body-lg text-foreground">{score.description}</p>
              </div>
            ))}
          </div>
        ) : (
          <div id={activePanelId} role="tabpanel" aria-labelledby={activeTabId} tabIndex={0}>
            <p className="text-body-lg text-foreground">{descriptionText}</p>
          </div>
        )}
      </div>
    </article>
  );
});
